import { axiosGraphQL } from '../api';

const REQUEST_CODE = `
  mutation RequestEmailSignInCode($email: String!, $language: String) {
    requestEmailSignInCode(email: $email, language: $language) { challengeId expiresAt resendAt }
  }
`;
const VERIFY_CODE = `
  mutation VerifyEmailSignInCode($challengeId: String!, $code: String!) {
    verifyEmailSignInCode(challengeId: $challengeId, code: $code) { signInLink }
  }
`;
const errors = {
  EMAIL_SIGN_IN_INPUT_INVALID: 'invalid_email',
  EMAIL_SIGN_IN_CODE_INVALID: 'email_code_invalid',
  EMAIL_SIGN_IN_CODE_EXPIRED: 'email_code_expired',
  EMAIL_SIGN_IN_ATTEMPTS_EXHAUSTED: 'email_code_attempts_exhausted',
  EMAIL_SIGN_IN_RATE_LIMITED: 'email_code_rate_limited',
  EMAIL_SIGN_IN_DELIVERY_FAILED: 'registration_email_send_failed',
  EMAIL_SIGN_IN_UNAVAILABLE: 'email_code_unavailable',
};

// Only known error codes leave this boundary. Axios errors can contain the code
// and the one-use Firebase credential in their request/response objects.
const publicFailure = (error) => {
  const detail = error?.response?.data?.errors?.[0] || error;
  let code = detail?.extensions?.code || detail?.code || detail?.message;
  try { code = JSON.parse(code)?.message || code; } catch (_) { /* ordinary code */ }
  const failure = new Error(errors[code] || 'email_code_unavailable');
  const retryAt = Date.parse(detail?.extensions?.retryAt);
  if (Number.isFinite(retryAt)) failure.retryAt = retryAt;
  return failure;
};
const request = async (query, variables, field) => {
  let response;
  try { response = await axiosGraphQL({ query, variables }, { noAuth: true, withoutClub: true }); }
  catch (error) { throw publicFailure(error); }
  if (response?.data?.errors?.length) throw publicFailure(response.data.errors[0]);
  const result = response?.data?.data?.[field];
  if (!result) throw new Error('email_code_unavailable');
  return result;
};

export const requestEmailSignInCode = async (email, language) => {
  const result = await request(REQUEST_CODE, { email: email.trim().toLowerCase(), language }, 'requestEmailSignInCode');
  const expiresAt = Date.parse(result.expiresAt);
  const resendAt = Date.parse(result.resendAt);
  if (typeof result.challengeId !== 'string' || !result.challengeId || result.challengeId.length > 512 ||
    !Number.isFinite(expiresAt) || !Number.isFinite(resendAt) || expiresAt <= Date.now() || resendAt >= expiresAt) {
    throw new Error('email_code_unavailable');
  }
  return { challengeId: result.challengeId, expiresAt: result.expiresAt, resendAt: result.resendAt };
};

export const verifyEmailSignInCode = async (challengeId, code) => {
  if (typeof code !== 'string' || !/^\d{6}$/.test(code)) throw new Error('email_code_invalid');
  const result = await request(VERIFY_CODE, { challengeId, code }, 'verifyEmailSignInCode');
  if (typeof result.signInLink !== 'string' || !result.signInLink) throw new Error('email_code_unavailable');
  return result.signInLink;
};

export const getEmailSignInCodeError = (error) => {
  if (Object.values(errors).includes(error?.message)) return error.message;
  if (['auth/invalid-action-code', 'auth/expired-action-code', 'auth/invalid-credential'].includes(error?.code)) return 'email_code_expired';
  if (error?.code === 'auth/too-many-requests') return 'email_code_rate_limited';
  if (error?.message === 'session_identity_changed') return 'session_identity_changed';
  return 'email_code_unavailable';
};
